import { call, put, select, takeLatest } from 'redux-saga/effects'

import { selectAuthData } from '@/containers/Login/AuthSelectors'
import { showSnackbar, SNACKBAR_TYPES } from '@/shared/tools/notifications/notification'
import { callApi } from '@/shared/tools/apiSagaHelperV2'

import {
  submitRequest,
  submitSuccess,
  submitFailure,
} from './nettingUpdaterSlice'

import { selectNettingUpdaterOrganizationId } from './nettingUpdaterSelectors'

export const nettingUpdaterActions = {
  SUBMIT: 'ORGANIZATIONS/NETTING_UPDATER/SUBMIT',
  SUBMIT_FTPS: 'ORGANIZATIONS/NETTING_UPDATER/SUBMIT_FTPS',
  SUBMIT_API: 'ORGANIZATIONS/NETTING_UPDATER/SUBMIT_API',
}

function* submitNettingUploadSaga({ enqueue, t, file }) {
  yield put(submitRequest())

  try {
    const { extraRoutes } = yield select(selectAuthData)
    const url = extraRoutes?.nettingProcess?.upload
    if (!url) {
      const message = t?.('netting.errors.missing_url') || 'Missing netting upload url'
      yield put(submitFailure({ error: message }))
      if (enqueue) yield call(showSnackbar, enqueue, { type: SNACKBAR_TYPES.ERROR, message })
      return
    }

    const organizationId = yield select(selectNettingUpdaterOrganizationId)
    if (!organizationId || !file) {
      const message = t?.('netting.errors.invalid_form') || 'Veuillez sélectionner un fichier'
      yield put(submitFailure({ error: message }))
      if (enqueue) yield call(showSnackbar, enqueue, { type: SNACKBAR_TYPES.WARNING, message })
      return
    }

    const formData = new FormData()
    formData.append('organization_id', String(organizationId))
    formData.append('file', file)

    const data = yield callApi({
      endpoint: url,
      method: 'POST',
      payload: formData,
      enqueue,
      showErrorSnackbar: true,
    })

    const message = data?.message || ''

    yield put(submitSuccess({ message }))

    if (enqueue) {
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.SUCCESS,
        message: message || (t?.('netting.success') || 'Upload lancé avec succès'),
      })
    }
  } catch (error) {
    const message = error?.message || 'Unknown error'
    yield put(submitFailure({ error: message }))
    if (enqueue) {
      yield call(showSnackbar, enqueue, { type: SNACKBAR_TYPES.ERROR, message })
    }
  }
}

function* submitNettingTriggerSaga({ enqueue, t, providerId, startDate, fileDate, limit }) {
  yield put(submitRequest())

  try {
    const { extraRoutes } = yield select(selectAuthData)
    const url = extraRoutes?.nettingProcess?.reprocess
    if (!url) {
      const message = t?.('netting.errors.missing_url') || 'Missing netting trigger url'
      yield put(submitFailure({ error: message }))
      if (enqueue) yield call(showSnackbar, enqueue, { type: SNACKBAR_TYPES.ERROR, message })
      return
    }

    if (!providerId) {
      const message = t?.('netting.errors.invalid_form') || 'Fournisseur manquant'
      yield put(submitFailure({ error: message }))
      if (enqueue) yield call(showSnackbar, enqueue, { type: SNACKBAR_TYPES.WARNING, message })
      return
    }

    const formatLocalDate = (d) =>
      d instanceof Date
        ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        : d

    const payload = { organization_id: providerId }
    if (startDate) {
      payload.from_date = formatLocalDate(startDate)
    }
    if (fileDate) {
      payload.file_date = formatLocalDate(fileDate)
    }
    if (limit) {
      payload.limit = limit
    }

    const data = yield callApi({
      endpoint: url,
      method: 'POST',
      payload,
      enqueue,
      showErrorSnackbar: true,
    })

    const message = data?.message || ''
    yield put(submitSuccess({ message }))

    if (enqueue) {
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.SUCCESS,
        message: message || (t?.('netting.success') || 'Traitement lancé avec succès'),
      })
    }
  } catch (error) {
    const message = error?.message || 'Unknown error'
    yield put(submitFailure({ error: message }))
    if (enqueue) {
      yield call(showSnackbar, enqueue, { type: SNACKBAR_TYPES.ERROR, message })
    }
  }
}

export default function* nettingUpdaterSagas() {
  yield takeLatest(nettingUpdaterActions.SUBMIT, submitNettingUploadSaga)
  yield takeLatest(nettingUpdaterActions.SUBMIT_FTPS, submitNettingTriggerSaga)
  yield takeLatest(nettingUpdaterActions.SUBMIT_API, submitNettingTriggerSaga)
}
